import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { login as apiLogin, register as apiRegister, logout as apiLogout, checkSession as apiCheckSession, type AuthUser, type LoginInput, type RegisterInput } from '@/lib/auth';
import { setAccessToken } from '@/lib/api';

interface AuthState {
  user: AuthUser | null;
  status: 'unknown' | 'authenticated' | 'unauthenticated';
  signIn: (input: LoginInput) => Promise<void>;
  signUp: (input: RegisterInput) => Promise<void>;
  signOut: () => Promise<void>;
}

const Ctx = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [status, setStatus] = useState<AuthState['status']>('unknown');

  useEffect(() => {
    // On boot, recover session cleanly via /auth/session (returns 200 without console 401 noise)
    (async () => {
      const sess = await apiCheckSession();
      if (sess) {
        setUser(sess.user);
        setStatus('authenticated');
      } else {
        setStatus('unauthenticated');
      }
    })();
  }, []);

  const value = useMemo<AuthState>(() => ({
    user,
    status,
    async signIn(input) {
      const r = await apiLogin(input);
      setUser(r.user);
      setStatus('authenticated');
    },
    async signUp(input) {
      const r = await apiRegister(input);
      setUser(r.user);
      setStatus('authenticated');
    },
    async signOut() {
      await apiLogout();
      setAccessToken(null);
      setUser(null);
      setStatus('unauthenticated');
    },
  }), [user, status]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
